import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, QueryStatus, useToast } from "@cx/ui";
import type { LabelTemplateInput, LabelTemplateView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import {
  LABEL_FIELDS,
  LABEL_LIMITS,
  LABEL_PAPER_LABELS,
  LABEL_PRESETS,
  LABEL_SOURCE_LABELS,
  LABEL_SOURCES,
  type LabelElement,
  type LabelLayout,
  type LabelSource,
} from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { postApiFile } from "../../download";
import { mmText, parseMm } from "./mm";

type Draft = { name: string; source: LabelSource; layout: LabelLayout };
const ELEMENT_LABEL: Record<LabelElement["type"], string> = {
  text: "Texto fixo",
  field: "Campo",
  barcode: "Código de barras",
  qrcode: "QR Code",
  line: "Linha",
};

function newElement(type: LabelElement["type"], source: LabelSource): LabelElement {
  switch (type) {
    case "text":
      return { type, x: 20, y: 20, w: 200, h: 40, text: "Texto", fontSize: 8, bold: false, align: "left" };
    case "field":
      return {
        type,
        x: 20,
        y: 20,
        w: 300,
        h: 40,
        field: "name",
        prefix: "",
        fontSize: 8,
        bold: false,
        align: "left",
        maxLines: 1,
      };
    case "barcode":
      return {
        type,
        x: 20,
        y: 20,
        w: 300,
        h: 120,
        field: source === "products" ? "gtin" : "code",
        symbology: source === "products" ? "ean13" : "code128",
        showText: true,
      };
    case "qrcode":
      return { type, x: 20, y: 20, size: 150, field: source === "products" ? "sku" : "code" };
    case "line":
      return { type, x: 20, y: 20, w: 200, h: 0, thickness: 3 };
  }
}

/** Campo em mm (uma casa); guarda décimos de mm. Texto inválido fica marcado e não altera o modelo. */
function MmInput({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange(v: number): void;
  min?: number;
}): ReactElement {
  const [text, setText] = useState(mmText(value));
  // Só reescreve o texto quando o valor mudou por fora (preset, outra fonte), não enquanto a pessoa digita "50,0".
  useEffect(() => {
    setText((t) => (parseMm(t) === value ? t : mmText(value)));
  }, [value]);
  const parsed = parseMm(text);
  const invalid = parsed === null || parsed < min || parsed > LABEL_LIMITS.maxSize;
  return (
    <label className="form-label small mb-0">
      {label}
      <input
        className={`form-control form-control-sm${invalid ? " is-invalid" : ""}`}
        inputMode="decimal"
        style={{ width: 80 }}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const v = parseMm(e.target.value);
          if (v !== null && v >= min && v <= LABEL_LIMITS.maxSize) onChange(v);
        }}
      />
    </label>
  );
}

function IntInput({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange(v: number): void;
}): ReactElement {
  return (
    <label className="form-label small mb-0">
      {label}
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ width: 80 }}
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isInteger(v) && v >= min && v <= max) onChange(v);
        }}
      />
    </label>
  );
}

/**
 * Editor de modelo de etiqueta (T3.6): tamanho, folha (Pimaco/A4/Carta) ou rolo térmico, e os elementos — texto fixo,
 * campo do cadastro, código de barras (EAN-13/Code 128), QR Code e linha — com posição em mm. A prévia é um PDF com o
 * registro de exemplo gerado pela API.
 */
export function LabelTemplateEditorPage(): ReactElement {
  const { id } = useParams({ strict: false });
  const { modelo } = useSearch({ strict: false }) as { modelo?: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const saved = useQuery({
    queryKey: ["label-templates", companyId, id],
    queryFn: () => api<LabelTemplateView>(`/label-templates/${id}`),
    enabled: !!id,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (draft) return;
    if (id) {
      if (saved.data)
        setDraft({ name: saved.data.name, source: saved.data.source, layout: saved.data.layout });
      return;
    }
    const p = LABEL_PRESETS.find((x) => x.id === modelo) ?? LABEL_PRESETS[0]!;
    setDraft({ name: p.name, source: p.source, layout: structuredClone(p.layout) as LabelLayout });
  }, [id, saved.data, modelo, draft]);
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const save = useMutation({
    mutationFn: (body: LabelTemplateInput) =>
      id
        ? api<LabelTemplateView>(`/label-templates/${id}`, { method: "PUT", body: JSON.stringify(body) })
        : api<LabelTemplateView>("/label-templates", {
            method: "POST",
            body: JSON.stringify(body),
            headers: { "Idempotency-Key": crypto.randomUUID() },
          }),
    onSuccess: (t) => {
      toast("success", "Modelo salvo");
      qc.invalidateQueries({ queryKey: ["label-templates", companyId] });
      if (!id) nav({ to: "/cadastros/etiquetas/$id", params: { id: t.id } });
    },
    onError: (e: Error) => setError(errorMessage(e)),
  });
  const showPreview = useMutation({
    mutationFn: (d: Draft) =>
      postApiFile(
        "/label-templates/preview",
        companyId,
        "previa.pdf",
        { source: d.source, layout: d.layout },
        {
          inline: true,
        },
      ),
    onSuccess: (r) => {
      setError(null);
      if ("url" in r && r.url) setPreview(r.url);
    },
    onError: (e: Error) => setError(errorMessage(e)),
  });

  if (!draft)
    return (
      <>
        <ContentHeader title="Modelo de etiqueta" breadcrumb={["Cadastros", "Etiquetas"]} />
        <QueryStatus loading error={saved.error} />
      </>
    );

  const canSave = can(perms, "label_templates", id ? "update" : "create");
  const l = draft.layout;
  const setLayout = (p: Partial<LabelLayout>) => setDraft({ ...draft, layout: { ...l, ...p } });
  const setEl = (i: number, p: Partial<LabelElement>) =>
    setLayout({ elements: l.elements.map((e, j) => (j === i ? ({ ...e, ...p } as LabelElement) : e)) });
  const fields = Object.entries(LABEL_FIELDS[draft.source]) as [string, string][];
  const fieldSelect = (i: number, value: string, only?: string[]) => (
    <label className="form-label small mb-0">
      Campo
      <select
        className="form-select form-select-sm"
        value={value}
        onChange={(e) => setEl(i, { field: e.target.value } as Partial<LabelElement>)}
      >
        {fields
          .filter(([k]) => !only || only.includes(k))
          .map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
      </select>
    </label>
  );

  return (
    <>
      <ContentHeader
        title={id ? draft.name || "Modelo de etiqueta" : "Novo modelo de etiqueta"}
        breadcrumb={["Cadastros", "Etiquetas", id ? "Editar" : "Novo"]}
        actions={
          <>
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={showPreview.isPending}
              onClick={() => showPreview.mutate(draft)}
            >
              Pré-visualizar
            </button>
            {id && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => nav({ to: "/cadastros/etiquetas/imprimir", search: { modelo: id } })}
              >
                Imprimir
              </button>
            )}
            {canSave && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={save.isPending}
                onClick={() => {
                  if (!draft.name.trim()) return setError("Informe o nome do modelo.");
                  save.mutate({ name: draft.name.trim(), source: draft.source, layout: draft.layout });
                }}
              >
                Salvar
              </button>
            )}
          </>
        }
      />
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <div className="row g-3">
        <div className="col-xl-7">
          <div className="box card p-3 mb-3">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="lbl-name">
                  Nome
                </label>
                <input
                  id="lbl-name"
                  className="form-control"
                  maxLength={80}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="lbl-source">
                  Registros
                </label>
                <select
                  id="lbl-source"
                  className="form-select"
                  value={draft.source}
                  disabled={!!id}
                  onChange={(e) => {
                    const source = e.target.value as LabelSource;
                    // Campos de outra fonte não valem: troca para o nome.
                    const elements = l.elements.map((el) =>
                      "field" in el && !Object.hasOwn(LABEL_FIELDS[source], el.field)
                        ? el.type === "barcode"
                          ? {
                              ...el,
                              field: source === "products" ? "sku" : "code",
                              symbology: "code128" as const,
                            }
                          : { ...el, field: source === "products" ? "sku" : "code" }
                        : el,
                    );
                    setDraft({ ...draft, source, layout: { ...l, elements } });
                  }}
                >
                  {LABEL_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {LABEL_SOURCE_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="box card p-3 mb-3">
            <h2 className="h6">Etiqueta e página</h2>
            <div className="d-flex flex-wrap gap-2 align-items-end">
              <MmInput
                label="Largura (mm)"
                value={l.width}
                min={LABEL_LIMITS.minSize}
                onChange={(width) => setLayout({ width })}
              />
              <MmInput
                label="Altura (mm)"
                value={l.height}
                min={LABEL_LIMITS.minSize}
                onChange={(height) => setLayout({ height })}
              />
              <label className="form-label small mb-0">
                Impressão
                <select
                  className="form-select form-select-sm"
                  aria-label="Tipo de página"
                  value={l.page.kind}
                  onChange={(e) =>
                    setLayout({
                      page:
                        e.target.value === "roll"
                          ? { kind: "roll" }
                          : {
                              kind: "sheet",
                              paper: "A4",
                              columns: 2,
                              rows: 5,
                              marginTop: 100,
                              marginLeft: 50,
                              gapX: 0,
                              gapY: 0,
                            },
                    })
                  }
                >
                  <option value="sheet">Folha (jato de tinta/laser)</option>
                  <option value="roll">Rolo (impressora térmica)</option>
                </select>
              </label>
              <label className="form-label small mb-0">
                ZPL (dpi)
                <select
                  className="form-select form-select-sm"
                  value={l.zplDpi}
                  onChange={(e) => setLayout({ zplDpi: Number(e.target.value) as 203 | 300 })}
                >
                  <option value={203}>203</option>
                  <option value={300}>300</option>
                </select>
              </label>
            </div>
            {l.page.kind === "sheet" && (
              <div className="d-flex flex-wrap gap-2 align-items-end mt-2">
                {(() => {
                  const pg = l.page;
                  const setPage = (p: Partial<typeof pg>) => setLayout({ page: { ...pg, ...p } });
                  return (
                    <>
                      <label className="form-label small mb-0">
                        Papel
                        <select
                          className="form-select form-select-sm"
                          value={pg.paper}
                          onChange={(e) => setPage({ paper: e.target.value as "A4" | "LETTER" })}
                        >
                          {(["A4", "LETTER"] as const).map((p) => (
                            <option key={p} value={p}>
                              {LABEL_PAPER_LABELS[p]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <IntInput
                        label="Colunas"
                        value={pg.columns}
                        min={1}
                        max={LABEL_LIMITS.maxColumns}
                        onChange={(columns) => setPage({ columns })}
                      />
                      <IntInput
                        label="Linhas"
                        value={pg.rows}
                        min={1}
                        max={LABEL_LIMITS.maxRows}
                        onChange={(rows) => setPage({ rows })}
                      />
                      <MmInput
                        label="Margem sup."
                        value={pg.marginTop}
                        onChange={(marginTop) => setPage({ marginTop })}
                      />
                      <MmInput
                        label="Margem esq."
                        value={pg.marginLeft}
                        onChange={(marginLeft) => setPage({ marginLeft })}
                      />
                      <MmInput label="Espaço horiz." value={pg.gapX} onChange={(gapX) => setPage({ gapX })} />
                      <MmInput label="Espaço vert." value={pg.gapY} onChange={(gapY) => setPage({ gapY })} />
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="box card p-3">
            <h2 className="h6">Elementos</h2>
            <p className="small text-muted">
              Posição a partir do canto superior esquerdo da etiqueta, em mm.
            </p>
            {l.elements.map((e, i) => (
              <div key={i} className="border rounded p-2 mb-2" data-testid="label-element">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong className="small">
                    {i + 1}. {ELEMENT_LABEL[e.type]}
                  </strong>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remover elemento ${i + 1}`}
                    disabled={l.elements.length === 1}
                    onClick={() => setLayout({ elements: l.elements.filter((_, j) => j !== i) })}
                  >
                    ×
                  </button>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <MmInput label="X" value={e.x} onChange={(x) => setEl(i, { x })} />
                  <MmInput label="Y" value={e.y} onChange={(y) => setEl(i, { y })} />
                  {e.type === "qrcode" ? (
                    <MmInput
                      label="Tamanho"
                      value={e.size}
                      min={50}
                      onChange={(size) => setEl(i, { size })}
                    />
                  ) : (
                    <>
                      <MmInput
                        label="Largura"
                        value={e.w}
                        min={e.type === "line" ? 0 : 1}
                        onChange={(w) => setEl(i, { w })}
                      />
                      <MmInput
                        label="Altura"
                        value={e.h}
                        min={e.type === "line" ? 0 : 1}
                        onChange={(h) => setEl(i, { h })}
                      />
                    </>
                  )}
                  {e.type === "text" && (
                    <label className="form-label small mb-0 flex-grow-1">
                      Texto
                      <input
                        className="form-control form-control-sm"
                        maxLength={LABEL_LIMITS.maxText}
                        value={e.text}
                        onChange={(ev) => setEl(i, { text: ev.target.value })}
                      />
                    </label>
                  )}
                  {e.type === "field" && (
                    <>
                      {fieldSelect(i, e.field)}
                      <label className="form-label small mb-0">
                        Prefixo
                        <input
                          className="form-control form-control-sm"
                          style={{ width: 90 }}
                          maxLength={LABEL_LIMITS.maxPrefix}
                          value={e.prefix}
                          onChange={(ev) => setEl(i, { prefix: ev.target.value })}
                        />
                      </label>
                      <IntInput
                        label="Linhas"
                        value={e.maxLines}
                        min={1}
                        max={LABEL_LIMITS.maxLines}
                        onChange={(maxLines) => setEl(i, { maxLines })}
                      />
                    </>
                  )}
                  {(e.type === "text" || e.type === "field") && (
                    <>
                      <IntInput
                        label="Fonte (pt)"
                        value={e.fontSize}
                        min={LABEL_LIMITS.minFont}
                        max={LABEL_LIMITS.maxFont}
                        onChange={(fontSize) => setEl(i, { fontSize })}
                      />
                      <label className="form-label small mb-0">
                        Alinhamento
                        <select
                          className="form-select form-select-sm"
                          value={e.align}
                          onChange={(ev) =>
                            setEl(i, { align: ev.target.value as "left" | "center" | "right" })
                          }
                        >
                          <option value="left">Esquerda</option>
                          <option value="center">Centro</option>
                          <option value="right">Direita</option>
                        </select>
                      </label>
                      <label className="form-check-label small">
                        <input
                          type="checkbox"
                          className="form-check-input me-1"
                          checked={e.bold}
                          onChange={(ev) => setEl(i, { bold: ev.target.checked })}
                        />
                        Negrito
                      </label>
                    </>
                  )}
                  {e.type === "barcode" && (
                    <>
                      <label className="form-label small mb-0">
                        Tipo
                        <select
                          className="form-select form-select-sm"
                          value={e.symbology}
                          onChange={(ev) => {
                            const symbology = ev.target.value as "ean13" | "code128";
                            setEl(i, symbology === "ean13" ? { symbology, field: "gtin" } : { symbology });
                          }}
                        >
                          {draft.source === "products" && <option value="ean13">EAN-13</option>}
                          <option value="code128">Code 128</option>
                        </select>
                      </label>
                      {fieldSelect(i, e.field, e.symbology === "ean13" ? ["gtin"] : undefined)}
                      <label className="form-check-label small">
                        <input
                          type="checkbox"
                          className="form-check-input me-1"
                          checked={e.showText}
                          onChange={(ev) => setEl(i, { showText: ev.target.checked })}
                        />
                        Mostrar número
                      </label>
                    </>
                  )}
                  {e.type === "qrcode" && fieldSelect(i, e.field)}
                  {e.type === "line" && (
                    <IntInput
                      label="Espessura (0,1 mm)"
                      value={e.thickness}
                      min={1}
                      max={20}
                      onChange={(thickness) => setEl(i, { thickness })}
                    />
                  )}
                </div>
              </div>
            ))}
            <div className="d-flex flex-wrap gap-2">
              {(Object.keys(ELEMENT_LABEL) as LabelElement["type"][]).map((t) => (
                <button
                  key={t}
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={l.elements.length >= LABEL_LIMITS.maxElements}
                  onClick={() => setLayout({ elements: [...l.elements, newElement(t, draft.source)] })}
                >
                  + {ELEMENT_LABEL[t]}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="col-xl-5">
          <div className="box card p-3 position-sticky" style={{ top: 16 }}>
            <h2 className="h6">Prévia</h2>
            {preview ? (
              <iframe
                title="Prévia da etiqueta"
                src={preview}
                style={{ width: "100%", height: 520, border: 0 }}
              />
            ) : (
              <p className="small text-muted mb-0">
                Clique em Pré-visualizar para gerar o PDF com um registro de exemplo.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
